import { useState, memo, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { HookResult, createDefaultHookResult } from '../../hooks/hookTypes'
import { useCreateProject } from '../../stores/projectsStore'
import { useGetOrganizations, Organization } from '../../stores/organizationsStore'
import { getStartingAngleDisplayName, getStartingAngleDescription } from '../../utils/projectUtils'

function NewProjectForm({ userId, userEmail, closeModal, onCreateNewProject }) {
  const [projectName, setProjectName] = useState('')
  const [projectDescription, setProjectDescription] = useState('')
  const [creativeness, setCreativeness] = useState(50)
  const [projectType, setProjectType] = useState('personal')
  const [startingAngle, setStartingAngle] = useState('quickstart')
  const [passwordProtected, setPasswordProtected] = useState(false)
  const [projectPassword, setProjectPassword] = useState('')
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')

  // Fetch user's organizations
  const organizationsStore = useGetOrganizations()
  const organizations: Organization[] = organizationsStore.organizations || []

  useEffect(() => {
    organizationsStore.fetchOrganizations()
  }, [])

  // Keep the existing createProject hook
  const createProject = (useCreateProject() ||
    createDefaultHookResult()) as HookResult
  const loading = createProject?.loading

  const handleCreateProject = async () => {
    if (!projectName.trim()) return

    try {
      const projectData = {
        userId: userId,
        userEmail: userEmail,
        projectName: projectName,
        projectDescription: projectDescription,
        creativeness: creativeness,
        projectType: projectType,
        startingAngle: startingAngle,
        passwordProtected: passwordProtected,
        projectPassword: passwordProtected ? projectPassword : null,
        organizationId: projectType === 'collaboration' ? selectedOrganizationId || null : null,
      }

      createProject.execute({
        data: projectData,
        force: true,
        onSuccess: (response) => {
          console.log('Project created successfully:', response)
          closeModal()
          onCreateNewProject(
            `Project '${projectData.projectName}' created successfully`,
            'success'
          )
          setProjectName('')
          setProjectDescription('')
          setProjectType('personal')
          setStartingAngle('quickstart')
          setPasswordProtected(false)
          setProjectPassword('')
          setSelectedOrganizationId('')
        },
        onError: (error) => {
          console.error('Error creating project:', error)
          onCreateNewProject(
            `Error creating project '${projectData.projectName}`,
            'error'
          )
        },
      })
    } catch (error) {
      console.error('Error creating project:', error)
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="projectName">Project Name</Label>
        <Input
          id="projectName"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="Enter project name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="projectDescription">Description</Label>
        <Textarea
          id="projectDescription"
          value={projectDescription}
          onChange={(e) => setProjectDescription(e.target.value)}
          rows={3}
          placeholder="Enter project description"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="projectType">Project Type</Label>
        <Select value={projectType} onValueChange={setProjectType}>
          <SelectTrigger>
            <SelectValue placeholder="Select project type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="personal">Personal - Private project for individual use</SelectItem>
            <SelectItem value="collaboration">Organization Project - Shared project for team work</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {projectType === 'collaboration' && (
        <div className="space-y-2">
          <Label htmlFor="organizationSelect">Organization</Label>
          <Select value={selectedOrganizationId} onValueChange={setSelectedOrganizationId}>
            <SelectTrigger>
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Select the organization this project will belong to. This cannot be changed after creation.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="startingAngle">Type of need</Label>
        <Select value={startingAngle} onValueChange={setStartingAngle}>
          <SelectTrigger>
            <SelectValue placeholder="Select the type of need" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="quickstart">{getStartingAngleDisplayName('quickstart')}</SelectItem>
            <SelectItem value="quickstartdynamic">{getStartingAngleDisplayName('quickstartdynamic')}</SelectItem>
            <SelectItem value="smartnotes">{getStartingAngleDisplayName('smartnotes')}</SelectItem>
            <SelectItem value="blank">{getStartingAngleDisplayName('blank')}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{getStartingAngleDescription(startingAngle)}</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Switch
            id="passwordProtected"
            checked={passwordProtected}
            onCheckedChange={setPasswordProtected}
          />
          <Label htmlFor="passwordProtected">Password protect this project</Label>
        </div>
        {passwordProtected && (
          <Input
            type="password"
            placeholder="Enter project password"
            value={projectPassword}
            onChange={(e) => setProjectPassword(e.target.value)}
          />
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="creativeness-slider">Creativeness Level</Label>
        <input
          id="creativeness-slider"
          type="range"
          min={0}
          max={100}
          step={1}
          value={creativeness}
          onChange={e => setCreativeness(Number(e.target.value))}
          className="w-4/5 my-3"
          title={`Creativeness level: ${creativeness}`}
        />
        <span className="text-xs text-gray-700 ml-2">{creativeness}</span>
        <div className="text-xs text-gray-700 mt-2 min-h-6">
          {creativeness <= 10 ? 'Very strict, deterministic, and literal. Output will be highly predictable and safe.' :
            creativeness <= 30 ? 'Conservative and focused. Output will be mostly safe, with some minor variation.' :
            creativeness <= 50 ? 'Balanced. Output will be a mix of creativity and reliability.' :
            creativeness <= 70 ? 'Creative and open. Output will be more original, with some risk of unexpected ideas.' :
            creativeness <= 90 ? 'Highly creative. Output will be imaginative, surprising, and less constrained.' :
            'Maximum creativity! Output may be wild, experimental, and very unpredictable.'}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateProject}
          disabled={loading || !projectName.trim() || (passwordProtected && !projectPassword.trim()) || (projectType === 'collaboration' && !selectedOrganizationId)}
        >
          Create Project
        </Button>
      </div>
    </div>
  )
}

export default memo(NewProjectForm)
